// src/routes/categories/picker/pickerModel.ts
import { buildTree, type Category } from '../categoryTree'

/** The rail's first entry: the categories this user tags most. */
export const MOST_USED_KEY = 'most-used'
/** A rail entry, not a second catalogue: twelve fill two tile rows on a phone. */
export const MOST_USED_MAX = 12
/** Search shows at most this many; past it the user refines the query. */
export const SEARCH_MAX = 60
/** The system row that means "no category". It is never a choice — "No category" is. */
const UNCATEGORIZED_KEY = 'misc.uncategorized'

export type PickerItem = { category: Category; groupName: string | null }
export type PickerGroup = { key: string; label: string; type: Category['category_type'] | null; items: PickerItem[] }
export type PickerModel = { groups: PickerGroup[]; byId: ReadonlyMap<string, PickerItem>; count: number }

/**
 * `GET /categories` + its `most_used` ids → what the picker draws.
 *
 * One group per TOP-LEVEL category, holding that category then its children —
 * the same two levels the Categories page shows, and the same set of choices
 * the old `<select>` offered (it listed parents too). Group order is
 * `buildTree`'s: Expense, Income, Transfer, each by `display_order`.
 */
export function buildPickerModel(categories: readonly Category[], mostUsed: readonly string[]): PickerModel {
  const usable = categories.filter((c) => c.is_active && c.key !== UNCATEGORIZED_KEY)
  const byId = new Map<string, PickerItem>()
  const groups: PickerGroup[] = []

  for (const segment of buildTree([...usable])) {
    for (const node of segment.nodes) {
      const root: PickerItem = { category: node.category, groupName: null }
      const children = node.children.map((c) => ({ category: c, groupName: node.category.name }))
      const items = [root, ...children]
      for (const item of items) byId.set(item.category.id, item)
      groups.push({ key: node.category.id, label: node.category.name, type: segment.type, items })
    }
  }

  const used = [...new Set(mostUsed)]
    .map((id) => byId.get(id))
    .filter((i): i is PickerItem => i !== undefined)
    .slice(0, MOST_USED_MAX)
  if (used.length > 0) groups.unshift({ key: MOST_USED_KEY, label: 'Most used', type: null, items: used })

  return { groups, byId, count: byId.size }
}

const fold = (s: string) => s.trim().toLocaleLowerCase()

/** 0 name starts with q · 1 a word in the name does · 2 name contains q · 3 parent name contains q. */
function rank(item: PickerItem, q: string): number | null {
  const name = fold(item.category.name)
  if (name.startsWith(q)) return 0
  if (name.split(/[^\p{L}\p{N}]+/u).some((w) => w.startsWith(q))) return 1
  if (name.includes(q)) return 2
  if (item.groupName !== null && fold(item.groupName).includes(q)) return 3
  return null
}

/** Every category whose name or parent matches, best first. Blank → none. */
export function searchPicker(model: PickerModel, query: string): PickerItem[] {
  const q = fold(query)
  if (q === '') return []
  const scored: { item: PickerItem; r: number }[] = []
  for (const item of model.byId.values()) {
    const r = rank(item, q)
    if (r !== null) scored.push({ item, r })
  }
  return scored
    .sort((a, b) => a.r - b.r || a.item.category.name.localeCompare(b.item.category.name))
    .slice(0, SEARCH_MAX)
    .map((s) => s.item)
}

/** The real group a category lives in — where the rail opens for an already-set row. */
export function groupKeyOf(model: PickerModel, id: string | null): string | null {
  if (id === null) return null
  const item = model.byId.get(id)
  if (item === undefined) return null
  return item.groupName === null ? item.category.id : (item.category.parent_id ?? null)
}
